// File: frontend/src/pages/Roadmap.tsx
import React from 'react';
import SEOHead from '../components/SEOHead';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    Fade,
    Container,
    Stack,
    styled,
    alpha,
    Chip,
    Link,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EmailIcon from '@mui/icons-material/Email';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ScienceIcon from '@mui/icons-material/Science';
import SchoolIcon from '@mui/icons-material/School';
import ArticleIcon from '@mui/icons-material/Article';
import { cyber } from '../theme';

const HeroSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
    border: `1px solid ${alpha(cyber.primary, 0.35)}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '300px',
        height: '300px',
        background: alpha(cyber.primaryLight, 0.25),
        borderRadius: '50%',
        filter: 'blur(80px)',
    }
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.25s ease',
    '&:hover': {
        transform: 'translateX(4px)',
        boxShadow: cyber.glow,
        borderColor: alpha(cyber.primary, 0.6),
    }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.2)})`,
    marginBottom: theme.spacing(2),
}));

const ContactCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    background: `linear-gradient(135deg, ${alpha(cyber.primary, 0.06)}, ${alpha(cyber.primaryLight, 0.06)})`,
    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
    borderRadius: theme.spacing(2),
}));

const Roadmap: React.FC = () => {
    const features = [
        {
            icon: <CloudUploadIcon sx={{ fontSize: 32, color: cyber.accent.cyan }} />,
            title: 'Expanded Data Ingestion',
            description: 'We are actively working on adding support for a wider range of OSINT sources. This also includes the ability to process new document types, such as PDF reports, to broaden the scope of our intelligence library.'
        },
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 32, color: cyber.accent.coral }} />,
            title: 'AI Agents for Weekly Recap and Newsletter',
            description: 'To help users stay ahead of the curve, we are building autonomous AI agents designed to automatically generate and distribute a weekly threat intelligence summary, synthesizing the most critical threats and emerging trends observed on the platform.'
        },
        {
            icon: <AccountTreeIcon sx={{ fontSize: 32, color: cyber.accent.green }} />,
            title: 'TI Knowledge Graph',
            description: 'This is our most ambitious feature. We are developing an LLM-generated knowledge graph to allow analysts to visually explore and query the complex relationships between threat data—connecting actors, malware, TTPs, and victim infrastructure in an intuitive, graph-based interface.'
        }
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <SEOHead
                title="Roadmap - TI Mindmap HUB"
                description="Explore upcoming features for TI Mindmap HUB: expanded OSINT ingestion, AI-powered weekly briefings, and threat intelligence knowledge graph."
                canonical="/roadmap"
            />
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Fade in timeout={500}>
                    <HeroSection>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                                <RocketLaunchIcon sx={{ fontSize: 36 }} />
                                <Typography variant="h4" component="h1" fontWeight="bold">
                                    Research Roadmap
                                </Typography>
                            </Stack>
                            <Typography variant="h6" sx={{ opacity: 0.95 }}>
                                Exploring the future of AI-powered threat intelligence
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                                <Chip
                                    icon={<ScienceIcon sx={{ color: 'white !important' }} />}
                                    label="Research Project"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                            </Stack>
                        </Box>
                    </HeroSection>
                </Fade>

                {/* Mission Statement */}
                <Fade in timeout={700}>
                    <Paper 
                        sx={{ 
                            p: 4, 
                            mb: 4, 
                            borderRadius: 2,
                            background: alpha(cyber.primary, 0.04),
                            border: `1px solid ${alpha(cyber.primary, 0.12)}`
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                            <ScienceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Research-Driven Development
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    Our primary goal is to{' '}
                                    <strong style={{ color: cyber.primaryLight }}>
                                        explore the automation of OSINT processing using Generative AI
                                    </strong>{' '}
                                    and understand both its capabilities and limitations. This project is strongly research-oriented, with a focus on reproducibility, transparency, and contributing knowledge to the cybersecurity community.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Fade>

                {/* Academic Collaboration */}
                <Fade in timeout={800}>
                    <Paper 
                        sx={{ 
                            p: 4, 
                            mb: 4, 
                            borderRadius: 2,
                            background: alpha(cyber.primary, 0.04),
                            border: `1px solid ${alpha(cyber.primary, 0.12)}`
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Academic Collaboration
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    Some of the features on this roadmap are developed in collaboration with universities and independent researchers. This includes ongoing work on STIX 2.1 generation and validation, as well as explorations into knowledge graph construction for threat intelligence.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                                    For more details on our research focus, see the <Link href="/research" color="primary">Research page</Link>.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Fade>

                {/* Features Section */}
                <Fade in timeout={900}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                            Upcoming Features
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            We are continuously working to enhance TI Mindmap HUB. Here are the key features and research areas we are currently focused on:
                        </Typography>

                        <List sx={{ p: 0 }}>
                            {features.map((feature, index) => (
                                <ListItem key={index} sx={{ p: 0, display: 'block' }}>
                                    <Fade in timeout={1000 + index * 200}>
                                        <FeatureCard elevation={2}>
                                            <IconWrapper>
                                                {feature.icon}
                                            </IconWrapper>
                                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                {feature.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                {feature.description}
                                            </Typography>
                                        </FeatureCard>
                                    </Fade>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Fade>

                {/* Research Publications */}
                <Fade in timeout={1400}>
                    <Paper 
                        sx={{ 
                            p: 4, 
                            mb: 4, 
                            borderRadius: 2,
                            background: alpha(cyber.primary, 0.04),
                            border: `1px solid ${alpha(cyber.primary, 0.12)}`
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <ArticleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Research Publications
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    We plan to publish findings from this research, including technical reports, methodology documentation, and peer-reviewed papers. Publications will be announced here and on our social channels.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: cyber.accent.amber }}>
                                    Coming soon.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Fade>

                {/* Get Involved Section */}
                <Fade in timeout={1600}>
                    <ContactCard elevation={3}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                            <HandshakeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                            <Typography variant="h5" fontWeight="bold">
                                Get Involved
                            </Typography>
                        </Stack>

                        <Typography variant="body1" paragraph color="text.secondary" sx={{ lineHeight: 1.8 }}>
                            TI Mindmap HUB is an experimental project that thrives on community feedback and collaboration.
                        </Typography>

                        <Typography variant="body1" paragraph color="text.secondary" sx={{ lineHeight: 1.8 }}>
                            We welcome:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                            <li><Typography variant="body1" color="text.secondary">Feedback on features and usability</Typography></li>
                            <li><Typography variant="body1" color="text.secondary">Academic collaboration proposals</Typography></li>
                            <li><Typography variant="body1" color="text.secondary">Research partnership inquiries</Typography></li>
                            <li><Typography variant="body1" color="text.secondary">Bug reports and suggestions</Typography></li>
                        </Box>

                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 3, 
                                mt: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: alpha(cyber.primary, 0.1),
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                borderRadius: 2
                            }}
                        >
                            <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Contact us at:
                                </Typography>
                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                    info@ti-mindmap-hub.com
                                </Typography>
                            </Box>
                        </Paper>
                    </ContactCard>
                </Fade>
            </Container>
        </Box>
    );
};

export default Roadmap;